import { css } from '@firebolt-dev/css'
import { ArrowUpIcon, CheckIcon, CopyIcon, LoaderIcon, Share2Icon, UnplugIcon, XIcon } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'wouter'
import moment from 'moment'

import { uuid } from '../shared/uuid'

import { cls } from './cls'
import { useAuth } from './useAuth'
import { isInputFocused } from './isInputFocused'
import { sleep } from '../shared/sleep'
import { copyToClipboard } from './copyToClipboard'
import { unpackURL } from '../shared/urls'
import { Logo } from './Logo'

export function Chat() {
  const auth = useAuth()
  const infoRef = useRef({
    pinnedToBottom: true,
    nextScrollSmooth: false,
    isMore: true,
    loadingMore: false,
    isDead: false,
    actions: null, // added below, provides read access to effects
  })
  const inputRef = useRef()
  const contentRef = useRef()
  const { simId } = useParams()
  const [sim, setSim] = useState(null)
  const [actions, setActions] = useState([])
  const [thinking, setThinking] = useState(false)
  const [text, setText] = useState('')
  const [connected, setConnected] = useState(false)
  const [share, setShare] = useState(false)

  infoRef.current.actions = actions

  useEffect(() => {
    const info = infoRef.current
    let autoScrolling = false

    function scrollToBottom(smooth) {
      info.pinnedToBottom = true
      window.scrollTo({
        top: 999999,
        behavior: smooth ? 'smooth' : 'auto',
      })
    }

    async function loadMore() {
      if (!info.connected || !info.actions.length || info.loadingMore || !info.isMore) return
      info.loadingMore = true

      try {
        const beforeId = info.actions[0].id
        const res = await auth.get(`/chats/${simId}/actions?beforeId=${beforeId}&limit=10`)

        if (info.isDead) return

        info.isMore = res.isMore

        if (res.actions.length) {
          const scrollHeight = document.documentElement.scrollHeight
          setActions(actions => [...res.actions, ...actions])
          requestAnimationFrame(() => {
            const newScrollHeight = document.documentElement.scrollHeight
            console.log({ scrollHeight, newScrollHeight })
            window.scrollTo(0, newScrollHeight - scrollHeight)
            info.loadingMore = false
          })
        } else {
          info.loadingMore = false
        }
      } catch (err) {
        console.error('failed to load more actions:', err)
        info.loadingMore = false
      }
    }

    // async function loadMore() {
    //   if (!info.connected || !info.actions.length) return
    //   info.loadingMore = true
    //   console.error('TODO: fetch older actions')
    //   // ws.send(
    //   //   JSON.stringify({
    //   //     type: 'loadMore',
    //   //     beforeId: info.actions[0].id,
    //   //   })
    //   // )
    // }

    function onScroll() {
      if (autoScrolling) return
      const scrollTop = window.scrollY
      const windowHeight = window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const threshold = 50
      info.pinnedToBottom = scrollTop + windowHeight >= documentHeight - threshold
      // top detection for loading more
      if (window.scrollY < 100 && info.isMore && !info.loadingMore) {
        loadMore()
      }
    }

    function onScrollEnd() {
      autoScrolling = false
    }

    function onContentChange() {
      if (!info.pinnedToBottom) return
      autoScrolling = true
      if (info.nextScrollSmooth) {
        info.nextScrollSmooth = false
        scrollToBottom(true) // smooth for new actions
      } else {
        scrollToBottom(false) // instant for layout shifts (images, resize)
      }
    }

    function onKeyDown(e) {
      if (e.key.length !== 1) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isInputFocused()) return
      inputRef.current.focus()
    }

    function onMessage(msg) {
      console.log('<- msg', msg)
      if (!info.connected) {
        info.connected = true
        setConnected(true)
      }
      if (msg.type === 'init') {
        setSim(msg.sim)
        // setUser(msg.user)
        info.nextScrollSmooth = false
        setActions(msg.actions)
      }
      if (msg.type === 'thinking') {
        setThinking(msg.value)
      }
      if (msg.type === 'action') {
        info.nextScrollSmooth = true
        setActions(actions => {
          actions = actions.slice()
          const replaceIdx = msg.confirmId ? actions.findIndex(action => action.id === msg.confirmId) : -1
          if (replaceIdx !== -1) {
            actions[replaceIdx] = msg.action
          } else {
            actions.push(msg.action)
          }
          return actions
        })
        if (msg.action.type === 'setName') {
          setSim(sim => {
            return {
              ...sim,
              name: msg.action.value,
            }
          })
        }
        if (msg.action.type === 'setImage') {
          setSim(sim => {
            return {
              ...sim,
              image: msg.action.value,
            }
          })
        }
      }
      // if (msg.type === 'olderActions') {
      //   info.loadingMore = false
      //   info.isMore = msg.isMore

      //   // Preserve scroll position when prepending
      //   const scrollHeight = document.documentElement.scrollHeight
      //   setActions(actions => [...msg.actions, ...actions])

      //   // After render, adjust scroll to maintain position
      //   requestAnimationFrame(() => {
      //     const newScrollHeight = document.documentElement.scrollHeight
      //     window.scrollTo(0, newScrollHeight - scrollHeight)
      //   })
      // }
      if (msg.type === 'error') {
        console.log('server error', msg.message)
      }
    }

    function onVisibilityChange() {
      console.log('onVisibilityChange')
    }

    // todo: how do we cancel this request on unmount?
    let cancel
    async function connect() {
      cancel = auth.stream(`/chats/${simId}/stream`, onMessage, async err => {
        // connection closed, reconnect in a sec
        console.error(err)
        info.connected = false
        setConnected(false)

        if (err === 'not_found') {
          return console.log('NO')
        }

        console.log('bro', document.visibilityState)

        // if document is hidden lets just chill here until they're back
        while (document.visibilityState !== 'visible') {
          console.log('hidden')
          await new Promise(resolve => setTimeout(resolve, 2000))
        }

        await new Promise(resolve => setTimeout(resolve, 2000))
        connect()
      })
    }
    connect()

    const observer = new ResizeObserver(onContentChange)
    observer.observe(document.documentElement) // was content
    window.addEventListener('scroll', onScroll)
    window.addEventListener('scrollend', onScrollEnd)
    window.addEventListener('keydown', onKeyDown)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('scrollend', onScrollEnd)
      window.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      info.isDead = true
      cancel?.()
    }
  }, [])

  const send = async () => {
    if (!connected) return
    if (!text) return
    const info = infoRef.current
    const action = {
      id: uuid(),
      entityId: auth.user.id,
      type: 'say',
      text,
      createdAt: moment().toISOString(),
    }
    info.nextScrollSmooth = true
    setActions(actions => {
      return [...actions, action]
    })
    setText('')
    inputRef.current.style.height = 'auto' // ensure input contracts back to empty size
    auth.post(`/chats/${simId}/actions`, action)
  }

  const visibleActions = useMemo(() => {
    // debug: show all
    // return actions
    //
    const types = new Set(['say', 'displayImage', 'typing'])
    return actions.filter(action => types.has(action.type))
  }, [actions])

  console.log(connected, visibleActions, sim, auth.user.id)

  return (
    <div
      className='chat'
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 100dvh;
        .gmu {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #f63939;
          z-index: 999;
        }
        .chat-header {
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          height: 60px;
          padding: 0 10px;
          display: flex;
          align-items: center;
          background: #16171a; // hsl(223deg 10% 14% / 95%);
          /* backdrop-filter: blur(5px); */
          &-side {
            width: 100px;
            display: flex;
            align-items: center;
            &.right {
              justify-content: flex-end;
            }
          }
          &-logo {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              max-width: 24px;
              height: auto;
            }
          }
          &-btn {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
              cursor: pointer;
            }
          }
          &-title {
            flex: 1;
            text-align: center;
            font-size: 21px;
            font-weight: 500;
          }
        }
        .chat-content {
          flex: 1;
          align-self: center;
          width: 100%;
          max-width: 740px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          padding: 20px 20px 0;
        }
        .chat-msg {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 0 60px 0 0;
          &-content {
            display: flex;
            justify-content: flex-start;
            width: 400px;
            max-width: 100%;
          }
          &-pfp {
            flex-shrink: 0;
            width: 42px;
            height: 42px;
            border-radius: 50%;
            background-color: #3f424b;
            background-position: center;
            background-size: cover;
            margin-right: 8px;
            &.hidden {
              opacity: 0;
            }
          }
          &-text {
            background: #3f424b;
            border-radius: 20px;
            padding: 10px 20px;
            line-height: 1.4;
            white-space: pre-wrap; // preserve line breaks
          }
          &-image {
            width: 400px;
            max-width: 100%;
            border-radius: 20px;
            overflow: hidden;
            background: #2b2c34;
            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }
          }
          &-json {
            background: #17191d;
            padding: 20px;
            border-radius: 20px;
            font-size: 13px;
            overflow-x: auto;
          }
          &-timestamp {
            padding: 3px 6px 6px 50px;
            font-size: 10px;
            color: rgba(255, 255, 255, 0.5);
            opacity: 0;
            &.visible {
              opacity: 1;
            }
          }
          &.user {
            padding: 0 0 0 60px;
            align-items: flex-end;
            .chat-msg-content {
              justify-content: flex-end;
            }
            .chat-msg-text {
              background: #2a2c33;
            }
            .chat-msg-timestamp {
              text-align: right;
            }
          }
          &.followup {
            margin-top: -14px;
          }
        }
        .chat-footer {
          padding: 0 20px 20px;
          position: sticky;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
        }
        .chat-footer-bg {
          background: #16171a;
          position: absolute;
          top: 27px;
          left: 0;
          right: 0;
          bottom: 0;
        }
        .chat-bar {
          width: 100%;
          max-width: 700px;
          min-height: 54px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
          background: #25282f;
          border-radius: 27px;
          border: 1px solid #30343c;
          display: flex;
          align-items: center;
          padding: 0 11px 0 20px;
          z-index: 1;
          cursor: text;
        }
        .chat-bar-input {
          flex: 1;
          width: 100%;
          field-sizing: content;
          resize: none;
          margin: 13px 0;
        }
        .chat-bar-send {
          margin-left: 20px;
          width: 36px;
          height: 36px;
          border-radius: 18px;
          background: #51545f;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #33353e;
          &.active {
            color: white;
          }
          &:hover {
            cursor: pointer;
          }
        }
        .chat-bar-reconnecting {
          margin-left: 20px;
          width: 36px;
          height: 36px;
          border: 1px solid #c62323;
          border-radius: 50%;
          background: #841515;
          display: flex;
          align-items: center;
          justify-content: center;
          svg {
            // todo:pulse
            animation: pulse 2s ease-in-out infinite;
            @keyframes pulse {
              0%,
              100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.6;
                transform: scale(0.9);
              }
            }
          }
        }
        .chat-connecting {
          position: fixed;
          inset: 0;
          background: #16171a;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}
    >
      {auth.user.gmu && <div className='gmu' />}
      <div className='chat-header'>
        <div className='chat-header-side'>
          <Link to='/'>
            <div className='chat-header-logo'>
              <Logo />
            </div>
          </Link>
        </div>
        <div className='chat-header-title'>{sim?.name || 'Sim'}</div>
        <div className='chat-header-side right'>
          <div className='chat-header-btn' onClick={() => setShare(true)}>
            <Share2Icon size={20} />
          </div>
        </div>
      </div>

      <div className='chat-content' ref={contentRef}>
        {visibleActions.map((action, idx) => (
          <Action
            key={action.id}
            sim={sim}
            action={action}
            prevAction={visibleActions[idx - 1]}
            nextAction={visibleActions[idx + 1]}
          />
        ))}
        {thinking && (
          <Message sim={sim} thinking={true} followup={visibleActions[visibleActions.length - 1].entityId === sim.id} />
        )}
        {connected && !visibleActions.length && sim.creatorId === auth.user.id && (
          <div
            css={css`
              background: #1e1f23;
              border-radius: 20px;
              padding: 20px;
              margin: 0 0 20px;
              p {
                text-align: center;
                color: rgba(255, 255, 255, 0.5);
                line-height: 1.4;
                margin: 0 0 10px;
              }
            `}
          >
            <p>
              Tell your sim who they are or let them choose! Your sim can set their own name, generate their own profile
              picture and learn new things.
            </p>
          </div>
        )}
      </div>
      <div className='chat-footer'>
        <div className='chat-footer-bg' />
        <label className='chat-bar'>
          <textarea
            className='chat-bar-input'
            ref={inputRef}
            type='text'
            value={text}
            placeholder='Say something'
            onChange={e => setText(e.target.value)}
            onInput={() => {
              const input = inputRef.current
              input.style.height = 'auto'
              input.style.height = input.scrollHeight + 'px'
            }}
            onKeyDown={e => {
              // console.log(e)
              if (e.code === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send()
              }
            }}
          />
          {connected && (
            <div className={cls('chat-bar-send', { active: !!text })} onClick={send}>
              <ArrowUpIcon size={20} />
            </div>
          )}
          {!connected && (
            <div className='chat-bar-reconnecting'>
              <UnplugIcon size={20} />
            </div>
          )}
        </label>
      </div>
      {!sim && (
        <div className='chat-connecting'>
          <Loading />
        </div>
      )}
      {share && <Share sim={sim} onClose={() => setShare(false)} />}
    </div>
  )
}

function Action({ sim, action, prevAction, nextAction }) {
  if (action.type === 'say') {
    return <SayAction sim={sim} action={action} prevAction={prevAction} nextAction={nextAction} />
  }
  if (action.type === 'displayImage') {
    return <DisplayImageAction sim={sim} action={action} prevAction={prevAction} nextAction={nextAction} />
  }
  return <HiddenAction sim={sim} action={action} prevAction={prevAction} nextAction={nextAction} />
}

function SayAction({ sim, action, prevAction, nextAction }) {
  return (
    <Message
      sim={action.entityId === sim.id ? sim : null}
      text={action.text}
      createdAt={action.createdAt}
      followup={isFollowup(action, prevAction)}
      ending={action.entityId !== nextAction?.entityId}
    />
  )
}

function DisplayImageAction({ sim, action, prevAction, nextAction }) {
  return (
    <Message
      sim={action.entityId === sim.id ? sim : null}
      imageUrl={action.url}
      imageRatio={action.width / action.height}
      createdAt={action.createdAt}
      followup={isFollowup(action, prevAction)}
      ending={action.entityId !== nextAction?.entityId}
    />
  )
}

function HiddenAction({ sim, action, prevAction, nextAction }) {
  // during dev we make these visible?
  // return null
  return (
    <Message
      sim={action.entityId === sim.id ? sim : null}
      json={action}
      createdAt={action.createdAt}
      followup={isFollowup(action, prevAction)}
      ending={action.entityId !== nextAction?.entityId}
    />
  )
}

function Message({ sim, thinking, text, imageUrl, imageRatio, json, createdAt, followup, ending }) {
  const [timeVisible, setTimeVisible] = useState(false)
  return (
    <div className={cls('chat-msg', { user: !sim, followup })}>
      <div className='chat-msg-content'>
        {sim?.image && (
          <div
            className={cls('chat-msg-pfp', { hidden: followup })}
            css={css`
              background-image: url(${unpackURL(sim.image)});
            `}
          />
        )}
        {thinking && (
          <div className='chat-msg-text'>
            <ThinkingDots />
          </div>
        )}
        {imageUrl && (
          <div
            className='chat-msg-image'
            css={css`
              aspect-ratio: ${imageRatio};
            `}
            onPointerEnter={() => setTimeVisible(true)}
            onPointerLeave={() => setTimeVisible(false)}
          >
            <img src={unpackURL(imageUrl)} />
          </div>
        )}
        {text && (
          <div
            className='chat-msg-text'
            onPointerEnter={() => setTimeVisible(true)}
            onPointerLeave={() => setTimeVisible(false)}
          >
            <span>{text}</span>
          </div>
        )}
        {json && (
          <div className='chat-msg-json noscrollbar'>
            <pre>{JSON.stringify(json, null, 2)}</pre>
          </div>
        )}
      </div>
      <div className={cls('chat-msg-timestamp', { visible: createdAt && timeVisible && ending })}>
        <span>{formatDate(createdAt)}</span>
      </div>
    </div>
  )
}

function ThinkingDots() {
  return (
    <span
      css={css`
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 0;
        margin: 7px 0 3px;
        .dot {
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: bounce 1.4s ease-in-out infinite;
        }
        .dot:nth-child(1) {
          animation-delay: 0s;
        }
        .dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes bounce {
          0%,
          60%,
          100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-6px);
          }
        }
      `}
    >
      <span className='dot' />
      <span className='dot' />
      <span className='dot' />
    </span>
  )
}

function isFollowup(action, prevAction) {
  return (
    prevAction &&
    action.entityId === prevAction?.entityId &&
    moment(action.createdAt).diff(prevAction.createdAt, 'minutes') <= 5
  )
}

function formatDate(isoString) {
  if (!isoString) return '-'
  const date = moment(isoString)
  const now = moment()
  if (date.isSame(now, 'day')) {
    // today: just show time
    return date.format('h:mma')
  } else if (date.isSame(now, 'year')) {
    // same year: show date without year
    return date.format('ddd, MMM D [at] h:mma')
  } else {
    // different year: show full date with year
    return date.format('ddd, MMM D YYYY [at] h:mma')
  }
}

function Loading() {
  return (
    <div
      css={css`
        height: 100dvh;
        display: flex;
        align-items: center;
        justify-content: center;
        svg {
          animation: spin 2s linear infinite;
          @keyframes spin {
            from {
              transform: rotate(0turn);
            }
            to {
              transform: rotate(1turn);
            }
          }
        }
      `}
    >
      <LoaderIcon size={24} />
    </div>
  )
}

function Share({ sim, onClose }) {
  const url = useMemo(() => new URL(`${globalThis.env.BASE_URL}/${sim.id}`), [])
  const shortURL = `${url.host}${url.pathname}`
  const [copied, setCopied] = useState(false)
  const copy = () => {
    copyToClipboard(url.href)
    setCopied(true)
  }
  useEffect(() => {
    const timerId = setTimeout(() => {
      setCopied(false)
    }, 3000)
    return () => {
      clearTimeout(timerId)
    }
  }, [copied])
  return (
    <div
      className='share'
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 100dvh;
        padding: 20px;
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(5px);
        display: flex;
        align-items: center;
        justify-content: center;
        .share-modal {
          width: 340px;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.6);
          background: #191b1e;
          border: 1px solid #25272c;
          border-radius: 20px;
          padding: 40px;
          position: relative;
        }
        .share-title {
          text-align: center;
          font-size: 21px;
          font-weight: 500;
          margin: 0 0 20px;
        }
        .share-text {
          text-align: center;
          margin: 0 0 40px;
          color: rgba(255, 255, 255, 0.6);
        }
        .share-url {
          border-radius: 20px;
          background: #2a2c33;
          height: 40px;
          padding: 0 16px;
          display: flex;
          align-items: center;
          span {
            flex: 1;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          svg {
            margin-left: 10px;
          }
          &:hover {
            cursor: pointer;
            background: #34363f;
          }
        }
        .share-close {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3a3d46;
          &:hover {
            cursor: pointer;
            color: white;
          }
        }
      `}
      onClick={e => {
        if (e.fromInside) return
        onClose()
      }}
    >
      <div className='share-modal' onClick={e => (e.fromInside = true)}>
        <div className='share-title'>Share Sim</div>
        <div className='share-text'>Share this Sim with others to chat with them.</div>
        <div className='share-url' onClick={copy}>
          <span>{shortURL}</span>
          {!copied && <CopyIcon size={16} />}
          {copied && <CheckIcon size={16} />}
        </div>
        <div className='share-close' onClick={onClose}>
          <XIcon size={20} />
        </div>
      </div>
    </div>
  )
}
